import { trackOnboardingV5HelpWithSelected } from "@/features/onboarding/analytics";
import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetFitnessProfile } from "@/hooks/requests/fitness-profile/use-get-fitness-profile";
import { useUpdateFitnessProfile } from "@/hooks/requests/fitness-profile/use-update-fitness-profile";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { useEffect, useRef, useState } from "react";

const OPTIONS = [
  { id: "track-food", label: "Track my food for me" },
  { id: "meal-suggestions", label: "Make meal suggestions" },
  { id: "nutrition-advice", label: "Give me nutrition advice and guidance" },
  { id: "training-performance", label: "Support my training & performance" },
  { id: "navigate-real-life", label: "Help me navigate real life" },
];

const HelpWithPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { fitnessProfile } = useGetFitnessProfile();
  const { updateFitnessProfileAsync, isUpdating } = useUpdateFitnessProfile();
  const { showError } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (fitnessProfile?.helpWith?.length) {
      setSelected(new Set(fitnessProfile.helpWith));
      hydratedRef.current = true;
    }
  }, [fitnessProfile?.helpWith]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleContinue = async () => {
    if (selected.size === 0 || isUpdating) return;
    triggerHaptic();
    const helpWith = Array.from(selected);
    try {
      // Only this screen's field — PUT merges, so the other answers are preserved.
      await Promise.all([preloadNextRoute(), updateFitnessProfileAsync({ helpWith })]);
      trackOnboardingV5HelpWithSelected(helpWith);
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What would you like me to help with?"
      subtitle="Choose as many as you like. You can change these anytime."
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={selected.size === 0 || isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {OPTIONS.map((option) => (
          <OnboardingOptionPill
            key={option.id}
            label={option.label}
            selected={selected.has(option.id)}
            onClick={() => toggle(option.id)}
          />
        ))}
      </div>
    </OnboardingStepShellV5>
  );
};

export default HelpWithPage;
